/* GlobalNarrator — Listens globally across the app.
 *
 * Activates with keyboard shortcut Ctrl+Alt+L (Cmd+Alt+L on Mac).
 * When active: reads each sentence from ANY focused input/textarea/contenteditable
 * as the user types (.!?\n triggers read).
 *
 * Uses browser SpeechSynthesis — free, instant, works offline.
 * Reuses narrator_state_v1 voice/rate/pitch preferences. */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';
import { loadShortcuts, actionForEvent } from '../utils/narratorShortcuts';

const STATE_KEY = 'narrator_state_v1';
const ENABLED_KEY = 'narrator_global_enabled_v1';
const LAST_SPOKEN_KEY = 'narrator_last_spoken_v1';

function readState() {
  try { return JSON.parse(localStorage.getItem(STATE_KEY) || '{}'); }
  catch { return {}; }
}

function writeState(patch) {
  try {
    const cur = readState();
    localStorage.setItem(STATE_KEY, JSON.stringify({ ...cur, ...patch }));
  } catch {/**/}
}

const SENTENCE_END_RE = /[.!?\n]/;
const WORD_END_RE = /[\s.,;:!?\n]/;

function isEditable(el) {
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag === 'input') {
    const t = (el.type || '').toLowerCase();
    return ['text', 'search', 'email', 'url', 'tel', 'password', ''].includes(t);
  }
  if (tag === 'textarea') return true;
  if (el.isContentEditable) return true;
  return false;
}

function getElementText(el) {
  if (!el) return '';
  if (el.isContentEditable) return el.innerText || '';
  return el.value || '';
}

function getCaretPos(el) {
  if (!el) return 0;
  if (el.isContentEditable) {
    const sel = window.getSelection?.();
    if (!sel || sel.rangeCount === 0) return 0;
    const range = sel.getRangeAt(0);
    const pre = range.cloneRange();
    pre.selectNodeContents(el);
    pre.setEnd(range.endContainer, range.endOffset);
    return pre.toString().length;
  }
  return el.selectionStart ?? 0;
}

export default function GlobalNarrator() {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(ENABLED_KEY) === '1'; }
    catch { return false; }
  });
  const [mode, setMode] = useState('sentence'); // 'sentence' | 'word'
  const prevTextMapRef = useRef(new WeakMap());

  // Persist enabled state
  useEffect(() => {
    try { localStorage.setItem(ENABLED_KEY, enabled ? '1' : '0'); } catch {/* */}
  }, [enabled]);

  // Speak a chunk using stored prefs
  const speak = useCallback((chunk) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const t = (chunk || '').trim();
    if (!t) return;
    const prefs = readState();
    const u = new SpeechSynthesisUtterance(t);
    const voices = window.speechSynthesis.getVoices();
    if (prefs.voiceURI) {
      const v = voices.find((vv) => vv.voiceURI === prefs.voiceURI);
      if (v) { u.voice = v; u.lang = v.lang; }
    } else {
      const sv = voices.find((v) => v.lang.startsWith('sv'));
      if (sv) { u.voice = sv; u.lang = sv.lang; }
    }
    u.rate = prefs.rate || 1.0;
    u.pitch = prefs.pitch || 1.0;
    u.volume = 1.0;
    try { localStorage.setItem(LAST_SPOKEN_KEY, t); } catch {/**/}
    window.speechSynthesis.speak(u);
  }, []);

  // Global keydown listener — uses user-configurable shortcuts
  useEffect(() => {
    let bindings = loadShortcuts();
    const reload = () => { bindings = loadShortcuts(); };
    window.addEventListener('narrator-shortcuts-changed', reload);

    const speakNow = (text) => {
      if (!text) return;
      try {
        window.speechSynthesis?.cancel();
        speak(text);
        try { localStorage.setItem(LAST_SPOKEN_KEY, text); } catch {/**/}
      } catch {/**/}
    };

    const onKey = (e) => {
      const action = actionForEvent(e, bindings);
      if (!action) return;
      // Most actions require Berättare to be enabled — except toggle/read-selected/open-panel
      const alwaysOn = ['toggle', 'read-selected', 'open-panel', 'stop', 'show-cheats'];
      if (!alwaysOn.includes(action) && !enabled) return;
      e.preventDefault();

      switch (action) {
        case 'toggle':
          setEnabled((v) => {
            const next = !v;
            toast.success(next
              ? '🎙️ Berättare-läge PÅ — lyssnar på dig nu'
              : '💤 Berättare-läge AV');
            if (!next) {
              try { window.speechSynthesis?.cancel(); } catch {/**/}
            }
            return next;
          });
          return;

        case 'mode-sentence':
          setMode('sentence');
          toast.success('📜 Mening-för-mening');
          return;

        case 'mode-word':
          setMode('word');
          toast.success('💬 Ord-för-ord');
          return;

        case 'read-selected': {
          const sel = window.getSelection?.().toString().trim();
          if (sel) {
            speakNow(sel);
            toast.success(`🔊 Läser upp ${sel.length} tecken`);
          } else {
            toast.info('Markera text först — sen tryck genvägen igen 💛');
          }
          return;
        }

        case 'pause-resume': {
          const synth = window.speechSynthesis;
          if (!synth) return;
          if (synth.speaking && !synth.paused) {
            synth.pause();
            toast.info('⏸️ Pausad');
          } else if (synth.paused) {
            synth.resume();
            toast.success('▶️ Fortsätter');
          }
          return;
        }

        case 'stop':
          try { window.speechSynthesis?.cancel(); } catch {/**/}
          toast.info('⏹️ Stoppad');
          return;

        case 'speed-up': {
          const cur = readState();
          const newRate = Math.min(2.0, (cur.rate || 1.0) + 0.1);
          writeState({ rate: newRate });
          toast.success(`⏩ Hastighet ${newRate.toFixed(1)}x`);
          return;
        }

        case 'speed-down': {
          const cur = readState();
          const newRate = Math.max(0.5, (cur.rate || 1.0) - 0.1);
          writeState({ rate: newRate });
          toast.success(`⏪ Hastighet ${newRate.toFixed(1)}x`);
          return;
        }

        case 'repeat-last': {
          let last = '';
          try { last = localStorage.getItem(LAST_SPOKEN_KEY) || ''; } catch {/**/}
          if (last) {
            speakNow(last);
            toast.success('🔁 Upprepar');
          } else {
            toast.info('Inget att upprepa än 💛');
          }
          return;
        }

        case 'open-panel':
          try {
            window.dispatchEvent(new CustomEvent('open-narrator-panel'));
          } catch {/**/}
          return;

        case 'show-cheats':
          try {
            window.dispatchEvent(new CustomEvent('open-narrator-cheatsheet'));
          } catch {/**/}
          return;

        default:
          return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('narrator-shortcuts-changed', reload);
    };
  }, [enabled, speak]);

  // While enabled, capture input events to detect new text
  useEffect(() => {
    if (!enabled) return;

    const onInput = (e) => {
      const el = e.target;
      if (!isEditable(el)) return;
      // Skip if inside the narrator panel (it has its own handler)
      if (el.closest?.('[data-testid="narrator-modal"]')) return;

      const current = getElementText(el);
      const prev = prevTextMapRef.current.get(el) || '';
      prevTextMapRef.current.set(el, current);

      if (current.length <= prev.length) return; // deletion

      const added = current.slice(prev.length);
      const lastChar = added[added.length - 1];

      if (mode === 'word' && WORD_END_RE.test(lastChar)) {
        const caret = getCaretPos(el);
        const upto = current.slice(0, caret - 1);
        const match = upto.match(/(\S+)$/);
        if (match) speak(match[1]);
      } else if (mode === 'sentence' && SENTENCE_END_RE.test(lastChar)) {
        const caret = getCaretPos(el);
        const upto = current.slice(0, caret - 1);
        let lastIdx = -1;
        for (let i = upto.length - 1; i >= 0; i--) {
          if (SENTENCE_END_RE.test(upto[i])) { lastIdx = i; break; }
        }
        const sentence = upto.slice(lastIdx + 1).trim();
        if (sentence) speak(sentence);
      }
    };

    document.addEventListener('input', onInput, true);
    return () => {
      document.removeEventListener('input', onInput, true);
      try { window.speechSynthesis?.cancel(); } catch {/* */}
    };
  }, [enabled, mode, speak]);

  // Floating indicator chip
  if (!enabled) return null;

  return (
    <button
      onClick={() => setEnabled(false)}
      className="fixed bottom-4 right-4 z-[200] neo-card px-3 py-2 bg-gradient-to-br from-[#FFC8DD] to-[#A2D2FF] flex items-center gap-2 shadow-lg animate-pulse-slow"
      data-testid="global-narrator-indicator"
      title="Klicka för att stänga av (eller Ctrl+Alt+L)"
    >
      <Mic className="w-4 h-4 text-[#D06892]" />
      <div className="text-left">
        <p className="text-[10px] font-bold uppercase tracking-wide leading-none">Berättare på</p>
        <p className="text-[9px] opacity-70 leading-tight">
          {mode === 'word' ? 'Ord-för-ord' : 'Mening-för-mening'} · Ctrl+Alt+L för av
        </p>
      </div>
      <MicOff className="w-3.5 h-3.5 opacity-60" />
    </button>
  );
}
